import {
  useCart,
  useDealer,
  useIsAdminMode,
  useSettings
} from "@/contexts";
import BorderColorOutlinedIcon from "@mui/icons-material/BorderColorOutlined";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import RemoveIcon from "@mui/icons-material/Remove";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddIcon from "@mui/icons-material/Add";
import TuneIcon from "@mui/icons-material/Tune";
import { CartHelper } from "@/helpers";
import { useDataApi } from "@/hooks";
import { CartProduct, Product } from "@/interfaces";
import { colors } from "@/theme";
import CancelIcon from "@mui/icons-material/Cancel";
import SearchIcon from "@mui/icons-material/Search";
import { Box, Button, TextField, useMediaQuery, useTheme } from "@mui/material";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import InputAdornment from "@mui/material/InputAdornment";
import Typography from "@mui/material/Typography";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ProductCartHeader
} from "../../molecules";
import CartTable from "../CartTable";
import ProductTable from "../ProductTable";
import * as S from "./styles";
import { formatCurrency } from "@/utils";

let isCalled = false

const ProductCart: React.FC = () => {
  const {
    cart,
    setDiscount,
    setDiscountTypeWithCartType,
    clear: clearCart,
    add,
    setQuantity: setProductItemQuantity,
    remove,
    setDiscountComment
  } = useCart();
  const { settings } = useSettings();
  const { dealer, coopInfo, setCoopInfo } = useDealer();
  // console.log("dealer", dealer);
  // console.log("coopInfo", coopInfo);
  const { setNewSession } = useDealer();
  const { setIsAdminMode } = useIsAdminMode();

  const { setProductPrice } = useCart();

  const [isOrderAdjustmentOpen, setIsOrderAdjustmentOpen] = useState(false);
  const [discountAmount, setDiscountAmount] = useState(
    cart?.discount ? cart.discount.toFixed(2) : "0.00"
  );
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchValue, setSearchValueLocal] = useState<string>("");
  const [searchProducts, setSearchProducts] = useState<Product[]>([]);
  const [searchPage, setSearchPage] = useState(1);
  const [hasMoreSearchPage, setHasMoreSearchPage] = useState(false);
  const [isSearching, setIsSearching] = useState(false);
  const searchTimeout = useRef<NodeJS.Timeout | null>(null);
  const baseUrl = `${process.env.REACT_APP_REST_BASE_URL}/products`;
  const {
    data: searchData,
    isLoading: isSearchLoading,
    error: searchError,
    setUrl: setSearchUrl,
  } = useDataApi(undefined);


  const [editedRetailPrice, setEditedRetailPrice] = useState(0);

  // console.log("searchData:", searchData);
  const [showSearchAddedProducts, setShowSearchAddedProducts] = useState(false);
  const [searchAddedProducts, setSearchAddedProducts] = useState<CartProduct[]>(
    []
  );
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<CartProduct | null>(
    null
  );
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState<CartProduct | null>(
    null
  );
  const navigate = useNavigate();
  // Filter out offsite products from cart for all calculations
  const filteredCart = cart ? {
    ...cart,
    products: cart.products?.filter(product => !product.offsite) || []
  } : undefined;
  const [discountValue, setDiscountValue] = useState(formatCurrency(filteredCart?.discount ?? 0));
  const [discount, setDiscountWithType] = useState({
    type: dealer?.percentageOff ? 'percentage' : 'amount' as 'percentage' | 'amount',
    value: dealer?.percentageOff ? dealer.percentageOff * 100 : 0
  });
  const [discountNote, setDiscountNote] = useState("");

  const orderOffSite = (filteredCart as any)?.offsite


  console.log("cart?.discountComment", cart?.discountComment)

  // Use CartHelper to check if there are in-store items (quantity > shippingQuantity)
  const hasInStoreItems = CartHelper.getInStoreItems(filteredCart).length > 0;

  const perBoxShippingFee = settings?.shippingFee || 0;
  const boxesToShip = CartHelper.getShippedItemsQuantity(filteredCart);

  // Handler to move all in-store items to shipping
  const handleShipAllInStore = () => {
    if (!filteredCart || !Array.isArray(filteredCart.products)) return;
    CartHelper.getInStoreItems(filteredCart).forEach((product) => {
      // Move all quantity to shippingQuantity
      if (setProductItemQuantity) {
        setProductItemQuantity(product.productId, (product.quantity || 0) + (product?.shippingQuantity || 0), 'shipping');
        setProductItemQuantity(product.productId, 0, "in-store");
      }
    });
  };

  const handleOrderAdjustmentOpen = () => {
    setDiscountAmount(filteredCart?.discount ? filteredCart.discount.toFixed(2) : "0.00");
    setIsOrderAdjustmentOpen(true);
  };


  // Fetch products when searchValue changes
  useEffect(() => {
    if (searchTimeout.current) clearTimeout(searchTimeout.current);
    if (!searchValue) {
      setSearchProducts([]);
      setIsSearching(false);
      return;
    }
    setIsSearching(true);
    setSearchProducts([]);
    setSearchPage(1);
    searchTimeout.current = setTimeout(() => {
      const url = `${baseUrl}?pageNum=1&pageSize=20&search=${encodeURIComponent(
        searchValue
      )}`;
      setSearchUrl(url);
    }, 400); // debounce
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchValue]);

  // Update products when searchData changes
  useEffect(() => {
    if (!isSearching) {
      console.log("🔍 Search not active — exiting early.");
      return;
    }

    if (isSearchLoading) {
      console.log("⏳ Still loading search data — exiting early.");
      return;
    }

    if (!searchData) {
      console.warn("⚠️ No search data available — exiting early.");
      return;
    }

    const filteredProducts = (searchData.data || []).filter((p: Product) => p.offsite === false);
    console.log("📦 Filtered products (offsite = false):", filteredProducts);

    setSearchProducts(filteredProducts);
    setHasMoreSearchPage(searchData?.totalPages > searchData?.page);

    // Auto-add product if there's only one result and it matches the search value
    if (filteredProducts.length === 1) {
      const product = filteredProducts[0];
      console.log("✅ Exactly one filtered product found:", product);

      if (product.sku === searchValue) {
        console.log(`🎯 Product SKU matches search value (${searchValue})`);

        const isCartEmpty = CartHelper.isEmpty(filteredCart);
        const hasOffsiteInCart = CartHelper.hasOffsiteProducts(filteredCart);
        // if (
        //   !isCartEmpty &&
        //   ((product.offsite && !CartHelper.hasOffsiteProducts(filteredCart)) ||
        //     (!product.offsite && CartHelper.hasOffsiteProducts(filteredCart))) ||
        //   (typeof product.inventoryCount === "number" && product.inventoryCount <= 0)
        // ) {
        //   alert("This product is out of Stock.")
        //   setSearchValueLocal("");
        //   setShowSearchAddedProducts(false);
        //   return;
        // }
        console.log("🛒 Cart empty:", isCartEmpty);
        console.log("🚫 Cart has offsite products:", hasOffsiteInCart);

        if (isCartEmpty || (!product.offsite && !hasOffsiteInCart)) {
          console.log("➕ Adding product to cart:", product);

          add?.(
            product,
            settings?.shippingFee,
            settings?.claimFee,
            // product.offsite
            //   ? coopInfo?.reimbursementRateOffSite
            //   : coopInfo?.reimbursementRateInStore,
            settings?.coopEligible,
            settings?.couponAmount
          );

          console.log("🧹 Clearing search value and hiding added products");
          setSearchValueLocal("");
          setShowSearchAddedProducts(false);
        } else {
          console.warn("🚫 Cannot add product due to cart content rules");
        }
      } else {
        console.log("❌ Product SKU does not match search value — not auto-adding");
      }
    } else {
      console.log(`🔎 ${filteredProducts.length} products found — not auto-adding`);
    }
  }, [isSearchLoading, isSearching, searchValue]);


  // Update useEffect to handle search state
  useEffect(() => {
    if (searchValue) {
      setShowSearchAddedProducts(true);
    } else {
      setShowSearchAddedProducts(false);
    }
  }, [searchValue]);

  const handleSearchInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValueLocal(e.target.value);
  };

  const handleCancelClick = () => {
    setSearchValueLocal("");
    setShowSearchAddedProducts(false);
  };

  const handleProductClick = (product?: Product) => {
    if (!product) return;
    // Only allow adding if cart is empty or product type matches cart
    // const isCartEmpty = CartHelper.isEmpty(filteredCart);
    // if (
    //   !isCartEmpty &&
    //   ((product.offsite && !CartHelper.hasOffsiteProducts(filteredCart)) ||
    //     (!product.offsite && CartHelper.hasOffsiteProducts(filteredCart))) ||
    //   (typeof product.inventoryCount === "number" && product.inventoryCount <= 0)
    // ) {
    //   alert("This product is out of Stock.")
    //   return;
    // }

    console.log("Adding product to cart:", product, coopInfo);
    add?.(
      product,
      settings?.shippingFee,
      settings?.claimFee,
      // product.offsite
      //   ? coopInfo?.reimbursementRateOffSite
      //   : coopInfo?.reimbursementRateInStore,
      settings?.coopEligible,
      settings?.couponAmount
    );
    // Add product to searchAddedProducts
    const cartProduct: CartProduct = {
      ...product,
      quantity: 1,
      coopApplied: settings?.coopEligible,
      // coopApplied: product.offsite
      //   ? coopInfo?.reimbursementRateOffSite
      //   : coopInfo?.reimbursementRateInStore,
    };
    setSearchAddedProducts((prev) => [...prev, cartProduct]);
    setShowSearchAddedProducts(true);
    setSearchValueLocal("")

  };

  const onProductItemQuantityChange = useCallback(
    (id: number, value: number) => {
      console.log({ id, value })
      if (value > 0) {
        setProductItemQuantity?.(id, value);
      } else {
        // Remove product if quantity is 0 or less
        setProductItemQuantity?.(id, 0);
      }
      handleAddDiscount()
    },
    [setProductItemQuantity]
  );

  const handleEditClick = (product: CartProduct) => {
    setEditingProduct(product);
    setEditedRetailPrice(product.price || 0); // <-- populate editable price
    setIsEditDialogOpen(true);
  };

  const handleAddDiscount = () => {
    let discountAmount = 0
    const total = Number(CartHelper.getSubtotalBalance(filteredCart) || 0);
    if (discount.type == "percentage") {
      const discountPercentage = discount.value || 0;
      discountAmount = total * discountPercentage / 100
    }
    else {
      discountAmount = discount.value || 0;
    }
    if (discountAmount > total) {
      alert("Discount cannot be greater than the subtotal amount");
      return;
    }
    
      setDiscountValue(`${discountAmount}`)
      setDiscountTypeWithCartType?.({storeType:"in-store",value:discount.value ?? 0,type:discount.type})
      setDiscountComment?.(discountNote)
      setDiscount?.(discountAmount);
      if (filteredCart) {
        if (filteredCart.discount !== discountAmount)
          filteredCart.discount = discountAmount;
      }
    setIsOrderAdjustmentOpen(false);
  }


  const handleEditDialogClose = () => {
    setIsEditDialogOpen(false);
    setEditingProduct(null);
  };

  const handleEditDialogSave = () => {
    console.log("Edit Dialog Save clicked");
    if (!editingProduct || !editingProduct.productId) return;

    setProductPrice(editingProduct.productId, editedRetailPrice); // ✅ updates price and recalculates co-op
    setIsEditDialogOpen(false);
    setEditingProduct(null);
  };


  const handleCheckoutClick = () => {
    navigate('/checkout', { state: { mode: 'in-store' } });
  };

  useEffect(() => {
    if (discount.type == "percentage") handleAddDiscount()
  }, [CartHelper.getSubtotalBalance(filteredCart)])

  return (
    <S.ProductCartContainer>
      <S.ProductCartWrapper>
        {/* Left Column */}
        <S.LeftColumn>
          {/* Top: Input Field */}
          <S.SearchContainer>
            <S.StyledTextField
              fullWidth
              placeholder="Search Product Name or Sku"
              variant="standard"
              size="small"
              autoFocus={true}
              value={searchValue}
              onChange={handleSearchInputChange}
              InputProps={{
                disableUnderline: true,
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon
                      sx={{ color: colors.neutralGrey800, fontSize: 24 }}
                    />
                  </InputAdornment>
                ),
                endAdornment: searchValue ? (
                  <InputAdornment position="end">
                    <CancelIcon
                      sx={{ color: colors.primaryOzone500, cursor: "pointer" }}
                      onClick={handleCancelClick}
                      style={{ width: 24, height: 24 }}
                    />
                  </InputAdornment>
                ) : null,
              }}
            />
          </S.SearchContainer>
          {/* Bottom: Cart or Searched Products */}
          <S.CartContainer>
            {searchValue ? (
              isSearchLoading ? (
                <S.LoadingContainer>
                  <span>Loading...</span>
                </S.LoadingContainer>
              ) : searchError ? (
                <Box sx={{ color: "red", textAlign: "center", mt: 2 }}>
                  Error loading products
                </Box>
              ) : (
                <ProductTable
                  items={searchProducts}
                  defaultProductImageUrl={settings?.defaultProductImageUrl}
                  coopEligible={coopInfo?.reimbursementRateInStore}
                  onItemClick={handleProductClick}
                />
              )
            ) : (
              <>
                {hasInStoreItems && (
                  <ProductCartHeader cart={filteredCart} onShipAll={handleShipAllInStore} />
                )}
                <CartTable
                  cart={filteredCart}
                  defaultProductImageUrl={settings?.defaultProductImageUrl}
                />
              </>
            )}
          </S.CartContainer>
        </S.LeftColumn>
        {/* Right Column */}
        <S.RightColumn>
          {/* Top: User Info */}
          <S.UserInfoContainer>
            <S.UserName>
              {dealer?.firstName} {dealer?.lastName}-{dealer?.company}
            </S.UserName>
            <S.UserDetails>
              {coopInfo?.channel} - {coopInfo?.masterDealerId} {" "} | {" "}
              {formatCurrency(coopInfo?.available || 0)} Co-Op Available
            </S.UserDetails>
          </S.UserInfoContainer>
          {/* Bottom: Purchase Summary */}
          <S.PurchaseSummaryContainer>
            <S.SummaryHeader>
              <S.SummaryTitle>Purchase Summary</S.SummaryTitle>
              <S.OrderNumber>
                {`Order Number # ${orderOffSite ? "" : (filteredCart?.orderId ?? "")}`}
              </S.OrderNumber>
            </S.SummaryHeader>
            {/* Purchase Summary Content */}
            {!showSearchAddedProducts ? (
              CartHelper.isEmpty(filteredCart) ? (
                <>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 10,
                      fontSize: "22px",
                    }}
                  >
                    <span>Items</span>
                    <span style={{ fontWeight: 500 }}>$00.00</span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 10,
                      fontSize: "22px",
                    }}
                  >
                    <span>Eligible co-op applied</span>
                    <span
                      style={{ color: colors.neutralGrey800, fontWeight: 500 }}
                    >
                      -$00.00
                    </span>
                  </div>
                  <div style={{ flex: 1 }} />
                  <S.SummaryDivider />
                  <S.TotalContainer>
                    <S.TotalLabel>
                      Total{" "}
                      <S.TotalItems>(0 items)</S.TotalItems>
                    </S.TotalLabel>
                    <S.TotalAmount>$00.00</S.TotalAmount>
                  </S.TotalContainer>
                </>
              ) : (
                <>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 20,
                      fontSize: 22,
                    }}
                  >
                    <span>Subtotal</span>
                    <span style={{ fontWeight: 500 }}>
                      {formatCurrency(CartHelper.getCartSubtotal(filteredCart) || 0)}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 20,
                      fontSize: 22,
                    }}
                  >
                    <span>Eligible Co-Op applied</span>
                    <span
                      style={{ color: colors.neutralGrey800, fontWeight: 500 }}
                    >
                      -{formatCurrency(Math.abs(CartHelper.getSubtotalCoopTotal(filteredCart)))}
                    </span>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 20,
                      fontSize: 22,
                    }}
                  >
                    <span>Discounts</span>
                    <span>
                      -{formatCurrency(filteredCart?.discount ?? 0)}
                    </span>
                  </div>
                  {/* <div
                    style={{
                      display: "flex",
                      justifyContent: "space-between",
                      marginBottom: 20,
                      fontSize: 22,
                    }}
                  >
                    <span>Shipping & handling</span>
                    <span>
                      +{(perBoxShippingFee * boxesToShip) > 0 ? formatCurrency(perBoxShippingFee * boxesToShip) : "$00.00"}
                    </span>
                  </div> */}
                  <div style={{ flex: 1 }} />
                  <S.SummaryDivider />
                  <S.TotalContainer>
                    <S.TotalLabel>
                      Total{" "}
                      <S.TotalItems>
                        ({CartHelper.getTotalQuantity(filteredCart)} items)
                      </S.TotalItems>
                    </S.TotalLabel>
                    <S.TotalAmount>
                      {formatCurrency(CartHelper.getTotalAmount({ ...filteredCart, products: filteredCart?.products || [], shippingFee: 0 }))}
                    </S.TotalAmount>
                  </S.TotalContainer>
                  <S.ButtonContainer>
                    <Button
                      variant="outlined"
                      fullWidth
                      sx={{
                        fontWeight: 500,
                        fontSize: 18,
                        fontFamily: "PF DIN Text Pro",
                        borderRadius: 2,
                        borderColor: colors.neutralGrey400,
                        color: colors.neutralGrey800,
                        textTransform: "none",
                        py: 1.5,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                      onClick={handleOrderAdjustmentOpen}
                    >
                      <BorderColorOutlinedIcon sx={{ mr: 1, fontSize: 20 }} />
                      Order Level Discount
                    </Button>
                    <Button
                      variant="contained"
                      fullWidth
                      sx={{
                        background: colors.primaryOzone500,
                        color: colors.white,
                        fontWeight: 500,
                        fontSize: 18,
                        fontFamily: "PF DIN Text Pro",
                        borderRadius: 2,
                        textTransform: "none",
                        py: 1.5,
                        boxShadow: "none",
                        ":hover": { background: "#0059c1" },
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                      onClick={handleCheckoutClick}
                    >
                      <CreditCardIcon sx={{ mr: 1, fontSize: 20 }} />
                      Checkout
                    </Button>
                  </S.ButtonContainer>
                </>
              )
            ) : (
              <>
                <div style={{
                  maxHeight: 'calc(100vh - 400px)',
                  overflowY: 'auto',
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  width: '100%'
                }}>
                  {[...CartHelper.getInStoreItems(filteredCart), ...CartHelper.getShippedItems(filteredCart)].map((product) => (
                    <div
                      key={product.productId}
                      style={{
                        display: "flex",
                        padding: "16px",
                        borderBottom: `1px solid ${colors.neutralGrey300}`,
                        position: "relative",
                        alignItems: "center",
                      }}
                    >
                      <img
                        src={
                          product.productImageUrl ||
                          settings?.defaultProductImageUrl
                        }
                        alt={product.productDescription}
                        style={{
                          width: 76,
                          height: 73,
                          objectFit: "contain",
                          marginRight: 14,
                        }}
                      />
                      <div style={{ flex: 1 }}>
                        {/* Title and Edit Icon */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginBottom: "2px",
                          }}
                        >
                          <div
                            style={{
                              fontSize: "16px",
                              fontWeight: 500,
                              color: colors.neutralGrey800,
                            }}
                          >
                            {product.productDescription}
                          </div>
                          <BorderColorOutlinedIcon
                            style={{
                              fontSize: 16,
                              color: colors.neutralGrey600,
                              cursor: "pointer",
                            }}
                            onClick={() => handleEditClick(product)}
                          />
                        </div>

                        {/* SKU */}
                        <div
                          style={{
                            fontSize: "14px",
                            color: colors.neutralGrey600,
                            marginBottom: 4,
                            fontFamily: "PF DIN Text Pro",
                          }}
                        >
                          SKU# {product.sku}
                        </div>

                        {/* Retail and Co-Op Prices */}
                        <div
                          style={{
                            display: "flex",
                            gap: 16,
                            marginBottom: 6,
                          }}
                        >
                          <span
                            style={{
                              fontSize: "14px",
                              color: colors.neutralGrey800,
                            }}
                          >
                            {formatCurrency(product.price || 0)} Retail
                          </span>
                          <span
                            style={{
                              fontSize: "14px",
                              color: colors.neutralGrey800,
                            }}
                          >
                            {formatCurrency(
                              (product.price || 0) * (1 - (filteredCart?.coopEligible || 0))
                            )}{" "}
                            w/ Co-Op
                          </span>
                        </div>

                        {/* Quantity Counter */}
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 12,
                          }}
                        >
                          <IconButton
                            size="small"
                            onClick={() => {
                              if (product.quantity > 1) {
                                onProductItemQuantityChange(
                                  product.productId,
                                  product.quantity - 1
                                );
                              } else {
                                setProductToDelete(product);
                                setIsDeleteDialogOpen(true);
                              }
                            }}
                            sx={{
                              borderRadius: "50%",
                              padding: "4px",
                            }}
                          >
                            {product.quantity > 1 ? (
                              <RemoveIcon fontSize="small" />
                            ) : (
                              <DeleteOutlineIcon fontSize="small" />
                            )}
                          </IconButton>
                          <span
                            style={{
                              fontSize: 16,
                              fontWeight: 500,
                              minWidth: "24px",
                              textAlign: "center",
                            }}
                          >
                            {product.quantity || 0}
                          </span>
                          <IconButton
                            size="small"
                            onClick={() =>
                              onProductItemQuantityChange(
                                product.productId,
                                (product.quantity || 0) + 1
                              )
                            }
                            sx={{
                              borderRadius: "50%",
                              padding: "4px",
                            }}
                          >
                            <AddIcon fontSize="small" />
                          </IconButton>
                        </div>
                      </div>

                      {/* Price - Bottom Right */}
                      <div
                        style={{
                          position: "absolute",
                          bottom: 16,
                          right: 16,
                          color: colors.primaryOzone500,
                          fontSize: "24px",
                          fontWeight: 500,
                          fontFamily: "PF DIN Text Pro",
                        }}
                      >
                        {formatCurrency(CartHelper.getItemInStoreSubtotal(product) - (product.coopInStore || 0))}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ flex: '0 0 auto' }}>
                  <S.SummaryDivider />
                  <S.TotalContainer>
                    <S.TotalLabel>
                      Total{" "}
                      <S.TotalItems>
                        ({CartHelper.getTotalQuantity(filteredCart)} items)
                      </S.TotalItems>
                    </S.TotalLabel>
                    <S.TotalAmount>
                      ${Number(CartHelper.getTotalAmount({ ...filteredCart, products: filteredCart?.products || [], shippingFee: perBoxShippingFee * boxesToShip }).toFixed(2)).toLocaleString()}
                    </S.TotalAmount>
                  </S.TotalContainer>
                  <S.ButtonContainer>
                    <Button
                      variant="outlined"
                      fullWidth
                      startIcon={<TuneIcon sx={{ height: 24, width: 24 }} />}
                      sx={{
                        fontWeight: 500,
                        fontSize: 18,
                        fontFamily: "PF DIN Text Pro",
                        borderRadius: 2,
                        borderColor: colors.neutralGrey400,
                        color: colors.neutralGrey800,
                        textTransform: "none",
                        py: 1.5,
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                      onClick={handleOrderAdjustmentOpen}
                    >
                      Order Level Discount
                    </Button>
                    <Button
                      variant="contained"
                      fullWidth
                      sx={{
                        background: colors.primaryOzone500,
                        color: colors.white,
                        fontWeight: 500,
                        fontSize: 18,
                        fontFamily: "PF DIN Text Pro",
                        borderRadius: 2,
                        textTransform: "none",
                        py: 1.5,
                        boxShadow: "none",
                        ":hover": { background: "#0059c1" },
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                      }}
                      onClick={handleCheckoutClick}
                    >
                      <CreditCardIcon sx={{ mr: 1, height: 24, width: 24 }} />
                      Checkout
                    </Button>
                  </S.ButtonContainer>
                </div>
              </>
            )}
          </S.PurchaseSummaryContainer>
        </S.RightColumn>
      </S.ProductCartWrapper>

      {/* Order Level Adjustment Dialog */}
      {/* <S.StyledDialog
        open={isOrderAdjustmentOpen}
        onClose={handleOrderAdjustmentClose}
        maxWidth="xs"
      >
        <S.DiscountDialogContainer>
          <S.DiscountDialogHeader>
            <span>Order Level Discount</span>
            <S.CloseCircleButton onClick={handleOrderAdjustmentClose}>
              <span style={{ color: '#fff', fontWeight: 700, fontSize: 20 }}>×</span>
            </S.CloseCircleButton>
          </S.DiscountDialogHeader>
          <S.DiscountDialogDivider />
          <S.DiscountDialogContent>
            <S.DiscountTabBar>
              <S.DiscountTab
                active={discountType === 'amount'}
                onClick={() => setDiscountType('amount')}
              >
                Amount
              </S.DiscountTab>
              <S.DiscountTab
                active={discountType === 'percentage'}
                onClick={() => setDiscountType('percentage')}
              >
                Percentage
              </S.DiscountTab>
            </S.DiscountTabBar>
            <S.DiscountInputWrapper>
              <S.DiscountInput
                variant="outlined"
                value={discountAmount}
                onChange={handleDiscountChange}
                InputProps={{
                  startAdornment: discountType === 'amount' ? <span style={{ color: '#b0b4bb', fontWeight: 500, fontSize: 22 }}>$</span> : undefined,
                  endAdornment: discountType === 'percentage' ? <span style={{ color: '#b0b4bb', fontWeight: 500, fontSize: 22 }}>%</span> : undefined,
                }}
                inputProps={{
                  inputMode: 'decimal',
                  style: { textAlign: 'center', fontSize: 24, fontWeight: 500, color: '#222b45' },
                }}
              />
            </S.DiscountInputWrapper>
            <S.DiscountNoteTextarea
              value={discountNote}
              onChange={e => setDiscountNote(e.target.value)}
              placeholder="Enter a note"
              rows={3}
            />
          </S.DiscountDialogContent>
          <Button
            onClick={handleApplyDiscount}
            fullWidth
            sx={{
              background: colors.primaryOzone500,
              color: colors.white,
              fontWeight: 500,
              fontSize: 18,
              fontFamily: "PF DIN Text Pro",
              borderRadius: "0 0 2px 2px",
              py: 1.5,
              boxShadow: "none",
              textTransform: "none",
              ":hover": { background: "#0059c1" },
            }}
          >
            Apply Discount
          </Button>
        </S.DiscountDialogContainer>
      </S.StyledDialog> */}

      <Dialog
        open={isOrderAdjustmentOpen}
        onClose={() => setIsOrderAdjustmentOpen(false)}
        maxWidth="xs"
        PaperProps={{
          sx: {
            borderRadius: '16px',
            p: 0,
            background: '#fff',
            boxShadow: 24,
            minWidth: 400,
            width: 400,
            overflow: 'visible',
          },
        }}
      >
        <Box sx={{ p: 0, width: '100%' }}>
          {/* Title and Close */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            position: 'relative',
            pt: 3,
            pb: 2,
          }}>
            <Typography sx={{
              fontWeight: 700,
              fontSize: 22,
              color: '#223354',
              fontFamily: 'PF DIN Text Pro',
              textAlign: 'center',
              flex: 1,
            }}>
              Order Level Discount
            </Typography>
            <IconButton
              onClick={() => setIsOrderAdjustmentOpen(false)}
              sx={{
                position: 'absolute',
                right: 24,
                top: '50%',
                transform: 'translateY(-50%)',
                background: colors.white,

              }}
            >
              <CancelIcon
                sx={{ color: colors.primaryOzone500, cursor: "pointer" }}
                style={{ width: 30, height: 30 }}
              />
            </IconButton>
          </Box>
          {/* Divider */}
          <Box sx={{ borderBottom: '1px solid #E0E0E0', mb: 0.5 }} />
          {/* Tabs */}
          <Box sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mt: 1,
            mb: 3,
            position: 'relative',
          }}>
            <Box
              sx={{
                fontWeight: discount.type === 'amount' ? 700 : 500,
                fontSize: 18,
                color: discount.type === 'amount' ? '#223354' : '#A0A4AD',
                fontFamily: 'PF DIN Text Pro',
                px: 2,
                pb: 1,
                cursor: 'pointer',
                borderBottom: discount.type === 'amount' ? '3px solid #0074FF' : '3px solid transparent',
                transition: 'border-color 0.2s',
                mr: 2,
              }}
              onClick={() => setDiscountWithType(prev => ({ ...prev, type: "amount" }))}
            >
              Amount
            </Box>
            <Box
              sx={{
                fontWeight: discount.type === 'percentage' ? 700 : 500,
                fontSize: 18,
                color: discount.type === 'percentage' ? '#223354' : '#A0A4AD',
                fontFamily: 'PF DIN Text Pro',
                px: 2,
                pb: 1,
                cursor: 'pointer',
                borderBottom: discount.type === 'percentage' ? '3px solid #0074FF' : '3px solid transparent',
                transition: 'border-color 0.2s',
              }}
              onClick={() => setDiscountWithType(prev => ({ ...prev, type: 'percentage' }))}
            >
              Percentage
            </Box>
            {/* Gray divider under tabs */}
            <Box sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              bottom: 0,
              height: '1px',
              bgcolor: '#E0E0E0',
              zIndex: 0,
            }} />
          </Box>
          {/* Input and Note */}
          <Box sx={{ px: 5, pb: 0, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <TextField
              fullWidth
              {...((discount.value==0 || discount.value)? ({value:discount.value}) : {})}
              placeholder={'0.00'}
              onChange={(e) => {
                const value = e.target.value.replace(/[^\d.]/g, "");
                const parts = value.split('.');
                if (parts.length > 2) return;
                if (parts[1] && parts[1].length > 2) return;
                if (value.startsWith('-')) return;
                setDiscountWithType(prev => ({ ...prev, value: Number(value) }))
              }}
              InputProps={{
                startAdornment: discount.type === 'amount' ? <span style={{ marginRight: 8, color: '#B0B4BB', fontWeight: 500, fontSize: 22 }}>$</span> : undefined,
                endAdornment: discount.type === 'percentage' ? <span style={{ marginLeft: 8, color: '#B0B4BB', fontWeight: 500, fontSize: 22 }}>%</span> : undefined,
                sx: {
                  alignSelf: 'center',
                  fontSize: 22,
                  width: '50%',
                  fontWeight: 500,
                  background: '#fff',
                  fontFamily: 'PF DIN Text Pro',
                  borderRadius: '12px',
                  height: 56,
                  // pl: 1,
                  '& input': {
                    textAlign: 'center',
                    fontSize: 22,
                    fontWeight: 500,
                    color: '#223354',
                    padding: '16px 0',
                  },
                },
              }}
              sx={{
                mb: 3,
                background: '#fff',
                borderRadius: '12px',
                boxShadow: 'none',
              }}
              inputProps={{
                inputMode: 'decimal',
              }}
            />
            <TextField
              fullWidth
              multiline
              rows={3}
              value = {discountNote}
              onChange={(e) => setDiscountNote(e.target.value)}
              placeholder="Enter a note"
              sx={{
                mb: 3,
                background: '#fff',
                borderRadius: '12px',
                boxShadow: 'none',
                border: '1px solid #E0E0E0',
                '& .MuiOutlinedInput-root': {
                  borderRadius: '12px',
                  background: '#fff',
                  '& fieldset': {
                    borderColor: '#E0E0E0',
                  },
                },
                '& textarea': {
                  fontFamily: 'PF DIN Text Pro',
                  fontSize: 16,
                },
              }}
            />
          </Box>
          {/* Button */}
          <Box sx={{ px: 0, pb: 0, pt: 0 }}>
            <Button
              onClick={handleAddDiscount}
              fullWidth
              sx={{
                background: '#0074FF',
                color: '#fff',
                borderRadius: '0 0 16px 16px',
                fontWeight: 500,
                fontSize: 20,
                fontFamily: 'PF DIN Text Pro',
                py: 2,
                boxShadow: 'none',
                textTransform: 'none',
                mt: 0,
                '&:hover': { background: '#0059c1' },
              }}
            >
              Add Discount
            </Button>
          </Box>
        </Box>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog
        open={isEditDialogOpen}
        onClose={handleEditDialogClose}
        maxWidth="xs"
        sx={{
          "& .MuiDialog-paper": {
            borderRadius: "8px",
            overflow: "hidden",
            padding: 0,
            width: "100%",
            maxWidth: "400px",
          },
        }}
      >
        <DialogTitle
          sx={{
            padding: "16px",
            position: "relative",
            textAlign: "center",
            fontFamily: "PF DIN Text Pro",
            fontSize: "18px",
            fontWeight: 500,
            color: colors.neutralGrey800,
          }}
        >
          Edit Item
          <IconButton
            onClick={handleEditDialogClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: colors.primaryOzone500,
            }}
          >
            <CancelIcon />
          </IconButton>
        </DialogTitle>
        {editingProduct && (
          <DialogContent sx={{ padding: 0 }}>
            <Box sx={{ p: 3 }}>
              <Box
                sx={{
                  width: "100%",
                  height: "200px",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  mb: 3,
                  bgcolor: colors.white,
                  border: `1px solid ${colors.neutralGrey200}`,
                  borderRadius: 1,
                }}
              >
                <img
                  src={
                    editingProduct.productImageUrl ||
                    settings?.defaultProductImageUrl
                  }
                  alt={editingProduct.productDescription}
                  style={{
                    maxWidth: "80%",
                    maxHeight: "80%",
                    objectFit: "contain",
                  }}
                />
              </Box>
              <Box sx={{ mb: 2 }}>
                <Typography
                  sx={{
                    fontSize: "22px",
                    fontWeight: 500,
                    color: colors.neutralGrey800,
                    mb: 1,
                    fontFamily: "PF DIN Text Pro",
                  }}
                >
                  {editingProduct.productDescription}
                </Typography>
                <Typography
                  sx={{
                    fontSize: "12px",
                    color: colors.neutralGrey600,
                    fontFamily: "PF DIN Text Pro",
                  }}
                >
                  SKU# {editingProduct.sku}
                </Typography>
              </Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  mb: 3,
                }}
              >
                <Typography
                  sx={{
                    fontSize: "16px",
                    fontWeight: 400,
                    color: colors.neutralGrey800,
                    fontFamily: "PF DIN Text Pro",
                  }}
                >
                  ${editingProduct.price?.toFixed(2)} Retail
                </Typography>
                <TextField
                  placeholder="Enter New Amount"
                  variant="outlined"
                  autoFocus
                  size="small"
                  value={editedRetailPrice}
                  onChange={(e) =>
                    setEditedRetailPrice(parseFloat(e.target.value) || 0)
                  }
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      handleEditDialogSave();
                    }
                  }}
                  sx={{
                    flex: 1,
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "4px",
                      backgroundColor: colors.white,
                      "& fieldset": {
                        borderColor: colors.neutralGrey300,
                      },
                    },
                    "& input": {
                      fontFamily: "PF DIN Text Pro",
                    },
                  }}
                />

              </Box>
            </Box>
          </DialogContent>
        )}
        <Button
          onClick={handleEditDialogSave}
          variant="contained"
          fullWidth
          sx={{
            bgcolor: colors.primaryOzone500,
            color: colors.white,
            py: 2,
            borderRadius: 0,
            fontSize: "16px",
            fontWeight: 500,
            textTransform: "none",
            fontFamily: "PF DIN Text Pro",
            "&:hover": {
              bgcolor: colors.primaryOzone500,
            },
          }}
        >
          Update Item
        </Button>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <S.StyledDialog
        open={isDeleteDialogOpen}
        onClose={() => setIsDeleteDialogOpen(false)}
        maxWidth="xs"
      >
        <DialogContent sx={{ textAlign: "center", pt: 4, pb: 2 }}>
          <Typography
            sx={{
              fontSize: 18,
              fontWeight: 500,
              mb: 2,
              fontFamily: "PF DIN Text Pro",
            }}
          >
            Are you sure you want to delete this item?
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", pb: 3 }}>
          <Button
            variant="outlined"
            onClick={() => setIsDeleteDialogOpen(false)}
            sx={{
              color: colors.neutralGrey800,
              borderColor: colors.neutralGrey800,
              minWidth: 100,
              borderRadius: 1.5,
              fontWeight: 500,
              fontSize: 18,
              textTransform: "none",
              mr: 2,
              fontFamily: "PF DIN Text Pro",
            }}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              if (productToDelete) {
                remove?.(productToDelete.productId);
              }
              setIsDeleteDialogOpen(false);
              setProductToDelete(null);
            }}
            sx={{
              minWidth: 100,
              borderRadius: 1.5,
              fontWeight: 400,
              fontSize: 18,
              textTransform: "none",
              background: "#E53912",
              color: "#fff",
              ":hover": { background: "#b71c00" },
              fontFamily: "PF DIN Text Pro",
            }}
          >
            Delete
          </Button>
        </DialogActions>
      </S.StyledDialog>
    </S.ProductCartContainer>
  );
};

export default ProductCart;
